import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Settings, Loader2, X, Check, ShieldCheck, ChevronDown, ChevronRight, Eye, EyeOff, Store, AlertTriangle, ExternalLink } from 'lucide-react';
import Layout from '../../../components/layout/Layout';
import Toggle from '../../../components/ui/Toggle';
import { useAuth } from '../../../context/AuthContext';
import { useError } from '../../../context/ErrorContext';
import { VerticalBadge } from '../../../components/admin/VerticalBadge';
import modulesApi, {
  type ModuleRow, type TenantInfo, type SettingsField, type ModuleManifestMeta,
  type PosPanelResponse, type PosTenantOption,
  isModuleActive, moduleAppliesToVertical,
} from '../../../api/modulesApi';

// Categoría de DISPLAY (más clara que el kind crudo):
//  - esenciales  = núcleo, siempre activos.
//  - listos      = compartidos YA terminados (tienen configuración) → Cumpleaños, Fidelidad, Campañas.
//  - preparacion = compartidos aún NO convergidos (sin configuración) → Plantillas…
//  - vertical    = propios del tipo de negocio.
// Orden de display: lo propio del negocio y lo listo primero; infra al final; lo que no aplica, hasta abajo.
const CAT_ORDER = ['vertical', 'listos', 'preparacion', 'esenciales', 'no_aplica'] as const;
const CAT_LABEL: Record<string, { title: string; desc: string }> = {
  esenciales:  { title: 'Esenciales',       desc: 'Siempre activos, la base del negocio.' },
  listos:      { title: 'Listos para usar', desc: 'Compartidos, terminados y configurables.' },
  preparacion: { title: 'En preparación',   desc: 'Compartidos que aún estamos terminando.' },
  vertical:    { title: 'De este negocio',  desc: 'Funciones propias de esta vertical.' },
  no_aplica:   { title: 'No aplican a este giro', desc: 'Módulos de otras verticales o funciones que este negocio no usa.' },
};
// Categorías cerradas por defecto (las menos accionables); las demás abren.
const CAT_COLLAPSED_DEFAULT: Record<string, boolean> = { esenciales: true, preparacion: true, no_aplica: true };
const VERTICAL_LABEL: Record<string, string> = {
  lealtad: 'Lealtad', dental: 'Dental', letsmove: 'Baile', clublectura: 'Club', pos: 'POS',
};

// Descripción breve por módulo (copy de UI). Fallback: sin texto si falta.
const MODULE_DESC: Record<string, string> = {
  // Esenciales (núcleo)
  reports: 'Reportes y estadísticas del negocio.',
  finance: 'Totales, ingresos y neto.',
  employees: 'Empleados y control de asistencia.',
  users_admin: 'Usuarios del panel y sus roles.',
  settings: 'Datos, marca y preferencias del negocio.',
  modules_admin: 'Activar y configurar los módulos.',
  billing: 'Plan, suscripción y facturación.',
  contacts: 'Directorio de clientes y contactos.',
  // Compartidos (cross)
  birthdays: 'Felicitación automática de cumpleaños (WhatsApp/correo).',
  perfil: 'Perfil del cliente: foto y datos, con autoservicio.',
  loyalty: 'Programa de puntos o cashback.',
  campaigns: 'Campañas y mensajes de seguimiento.',
  templates: 'Plantillas de mensajes de WhatsApp.',
  whatsapp: 'Conexión de WhatsApp: vincular número (QR) y enviar recordatorios/mensajes.',
  giftcards: 'Tarjetas de regalo.',
  cashbox: 'Turnos y cortes de caja.',
  vitrina: 'Catálogo de productos de mostrador y venta desde la caja.',
  // Dental
  dental_agenda: 'Agenda de citas.',
  dental_records: 'Expediente clínico del paciente.',
  dental_odontogram: 'Odontograma.',
  dental_doctors: 'Directorio de doctores y especialistas.',
  dental_consents: 'Documentos clínicos: justificantes, consentimientos y contratos.',
  dental_services: 'Catálogo de servicios y precios.',
  dental_fees: 'Honorarios y reparto por doctor.',
  dental_constancias: 'Importar y clasificar constancias (flujo en desuso).',
  dental_wa_logs: 'Registro de mensajes de WhatsApp enviados.',
  // Let's Move (baile)
  letsmove_classes: 'Clases y asistencia.',
  letsmove_events: 'Eventos y presentaciones.',
  letsmove_finances: 'Finanzas de la academia.',
  letsmove_members: 'Alumnos y sus perfiles.',
  letsmove_memberships: 'Planes y descuentos.',
  letsmove_messages: 'Mensajería interna.',
  letsmove_payroll: 'Nómina de maestros.',
  letsmove_settings: 'Ajustes del estudio.',
  letsmove_tasks: 'Tareas y notas.',
  letsmove_teachers: 'Profesores.',
  // Club de lectura
  clublectura_club: 'Libros, votaciones y calificaciones.',
  clublectura_venue: 'Votación de sede o lugar.',
  clublectura_admin: 'Administración del club.',
  // POS
  pos_sale: 'Terminal de venta.',
  pos_catalog: 'Catálogo y precios.',
  pos_inventory: 'Inventario.',
};
// Cross YA terminados cuya configuración NO es un drawer de settings sino un flujo
// aparte (p.ej. 'whatsapp': la conexión/QR vive en la app de agenda / la pantalla
// WhatsApp del manager; 'campaigns': motor unificado 16-jul-2026, se administra en
// su propia página por fachada; 'cashbox' y 'vitrina' (29-jul-2026): la config vive
// en cada app — Mi consultorio→Caja en la agenda, "Configurar caja"/página Vitrina
// en letsmove). Van a "Listos para usar" aunque no tengan schema.
const READY_NO_SETTINGS = new Set(['whatsapp', 'campaigns', 'cashbox', 'vitrina']);

function moduleCategory(m: ModuleRow, hasSettings: boolean): string {
  if (m.kind === 'core' || m.is_core === 1) return 'esenciales';
  if (m.kind === 'vertical') return 'vertical';
  if (m.kind === 'cross' && (hasSettings || READY_NO_SETTINGS.has(m.module_key))) return 'listos';
  return 'preparacion';
}

// ¿El campo es visible según showIf (pista declarativa del manifest)?
function isFieldVisible(f: SettingsField, values: Record<string, unknown>): boolean {
  if (!f.showIf) return true;
  return Object.entries(f.showIf).every(([k, allowed]) => allowed.includes(values[k]));
}

// ────────────────────────────────────────────────────────────────────────────
// Drawer de configuración de un módulo (form autogenerado por el schema)
// ────────────────────────────────────────────────────────────────────────────
const SettingsDrawer: React.FC<{
  tenantId: string; moduleKey: string; onClose: () => void; onSaved: () => void;
}> = ({ tenantId, moduleKey, onClose, onSaved }) => {
  const { addError } = useError();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [label, setLabel] = useState(moduleKey);
  const [schema, setSchema] = useState<SettingsField[]>([]);
  const [values, setValues] = useState<Record<string, unknown>>({});

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await modulesApi.getModuleSettings(tenantId, moduleKey);
        if (!alive) return;
        setLabel(r.label || moduleKey);
        setSchema(r.schema || []);
        setValues(r.settings || {});
      } catch (e: any) {
        if (alive) addError(`No se pudo cargar la configuración: ${e.message}`, undefined, 'modSettings');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [tenantId, moduleKey, addError]);

  const setVal = (key: string, v: unknown) => setValues((prev) => ({ ...prev, [key]: v }));

  const save = async () => {
    setSaving(true);
    try {
      // Solo enviamos los campos visibles del schema (no claves internas).
      const payload: Record<string, unknown> = {};
      for (const f of schema) if (isFieldVisible(f, values)) payload[f.key] = values[f.key];
      const r = await modulesApi.saveModuleSettings(tenantId, moduleKey, payload);
      setValues(r.settings || values);
      onSaved();
      onClose();
    } catch (e: any) {
      addError(`No se pudo guardar: ${e.message}`, undefined, 'modSettingsSave');
    } finally {
      setSaving(false);
    }
  };

  // Agrupa por section (preservando orden de aparición).
  const sections = useMemo(() => {
    const map = new Map<string, SettingsField[]>();
    for (const f of schema) {
      const s = f.section || '';
      if (!map.has(s)) map.set(s, []);
      map.get(s)!.push(f);
    }
    return Array.from(map.entries());
  }, [schema]);

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="modal-overlay absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-md bg-canvas h-full shadow-xl overflow-y-auto modal-drawer flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-line bg-surface sticky top-0">
          <div className="flex items-center gap-2 min-w-0">
            <Settings className="h-5 w-5 text-accent flex-shrink-0" />
            <h2 className="text-base font-semibold text-content truncate">Configurar: {label}</h2>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-surface-2"><X className="h-5 w-5 text-content-3" /></button>
        </div>

        <div className="flex-1 p-4 space-y-5">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-content-3"><Loader2 className="h-6 w-6 animate-spin mr-2" />Cargando…</div>
          ) : schema.length === 0 ? (
            <p className="text-sm text-content-3">Este módulo no tiene campos configurables.</p>
          ) : (
            sections.map(([section, fields]) => (
              <div key={section || '_'} className="space-y-3">
                {section && <h3 className="text-xs font-semibold uppercase tracking-wider text-content-3">{section}</h3>}
                {fields.filter((f) => isFieldVisible(f, values)).map((f) => (
                  <FieldControl key={f.key} field={f} value={values[f.key]} onChange={(v) => setVal(f.key, v)} />
                ))}
              </div>
            ))
          )}
        </div>

        <div className="px-4 py-3 border-t border-line bg-surface sticky bottom-0 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 text-sm rounded-md border border-line text-content-2 hover:bg-surface-2">Cancelar</button>
          <button onClick={save} disabled={saving || loading}
            className="px-4 py-2 text-sm rounded-md bg-accent-solid text-white hover:bg-accent-solid/90 disabled:opacity-50 flex items-center">
            {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Check className="h-4 w-4 mr-1.5" />}Guardar
          </button>
        </div>
      </div>
    </div>
  );
};

// Un control de formulario según el tipo del campo.
const FieldControl: React.FC<{ field: SettingsField; value: unknown; onChange: (v: unknown) => void }> = ({ field: f, value, onChange }) => {
  const inputCls = 'block w-full px-3 py-2 text-sm border border-line bg-surface rounded-md focus:outline-none focus:ring-1 focus:ring-accent focus:border-accent';
  const labelEl = <label className="block text-sm font-medium text-content mb-1">{f.label}</label>;
  const helpEl = f.help ? <p className="text-xs text-content-3 mt-1">{f.help}</p> : null;

  if (f.type === 'bool') {
    return (
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0"><div className="text-sm font-medium text-content">{f.label}</div>{helpEl}</div>
        <Toggle checked={!!value} onChange={onChange} label={f.label} />
      </div>
    );
  }
  if (f.type === 'enum') {
    return (
      <div>{labelEl}
        <select value={String(value ?? '')} onChange={(e) => onChange(e.target.value)} className={inputCls}>
          {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>{helpEl}
      </div>
    );
  }
  if (f.type === 'number') {
    return (
      <div>{labelEl}
        <input type="number" value={value === null || value === undefined ? '' : (value as number)}
          min={f.min} max={f.max} step={f.step ?? (f.format === 'int' ? 1 : 'any')}
          onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))} className={inputCls} />{helpEl}
      </div>
    );
  }
  if (f.type === 'textarea') {
    return (
      <div>{labelEl}
        <textarea value={String(value ?? '')} maxLength={f.maxLength} rows={4}
          onChange={(e) => onChange(e.target.value)} className={inputCls} />{helpEl}
      </div>
    );
  }
  // text / url / email / phone / color / image / time / ref → input de texto (URL para image en v1).
  return (
    <div>{labelEl}
      <input type={f.type === 'email' ? 'email' : f.type === 'url' || f.type === 'image' ? 'url' : 'text'}
        value={String(value ?? '')} placeholder={f.placeholder} maxLength={f.maxLength}
        onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)} className={inputCls} />{helpEl}
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// Página principal
// ────────────────────────────────────────────────────────────────────────────
// ────────────────────────────────────────────────────────────────────────────
// Bloque POS — el punto de venta vive en otro servicio y otra base (la caja tiene que
// cobrar aunque esta API esté caída), pero su GOBIERNO se hace desde aquí: así los
// clientes con POS se administran en el mismo sitio que los de lealtad o dental, en vez
// de en un panel aparte. Base hace de puente (lib/posBridge.js).
//
// Carga APARTE del resto de la página, a propósito: si el POS tarda o está reiniciando,
// la ficha de módulos se abre igual y aquí sale un aviso.
// ────────────────────────────────────────────────────────────────────────────
// Badge de salud de la integración de lealtad (pieza 5): "enlazado" y "funcionando" son cosas
// distintas — el 16-ago la ficha decía enlazado mientras la lealtad llevaba el día muda.
const haceCuanto = (iso: string) => {
  const min = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (min < 60) return `hace ${min} min`;
  const h = Math.round(min / 60);
  return h < 48 ? `hace ${h} h` : `hace ${Math.round(h / 24)} días`;
};
const PosHealthBadge: React.FC<{ h: NonNullable<PosPanelResponse['health']> }> = ({ h }) => {
  const cfg = {
    ok: { dot: 'bg-green-500', cls: 'text-green-700 dark:text-green-300', txt: 'Lealtad funcionando' },
    quieto: { dot: 'bg-amber-500', cls: 'text-amber-700 dark:text-amber-300', txt: 'Lealtad sin actividad reciente' },
    sin_actividad: { dot: 'bg-red-500', cls: 'text-red-700 dark:text-red-300', txt: 'Lealtad SIN actividad' },
    nunca: { dot: 'bg-amber-500', cls: 'text-amber-700 dark:text-amber-300', txt: 'Lealtad sin estrenar' },
    sin_clave: { dot: 'bg-gray-400', cls: 'text-content-3', txt: 'Lealtad sin clave API emitida' },
  }[h.status];
  return (
    <p className={`text-xs mt-0.5 normal-case tracking-normal font-normal flex items-center gap-1.5 ${cfg.cls}`}>
      <span className={`inline-block h-2 w-2 rounded-full flex-shrink-0 ${cfg.dot}`} />
      <span>
        {cfg.txt}
        {h.last_activity_at ? ` · última actividad ${haceCuanto(h.last_activity_at)}` : ''}
        {h.status === 'ok' || h.status === 'quieto'
          ? ` · ${h.tokens_24h ?? 0} venta(s) y ${h.visits_24h ?? 0} visita(s) en 24 h` : ''}
      </span>
    </p>
  );
};

const PosSection: React.FC<{ tenantId: string }> = ({ tenantId }) => {
  const { addError } = useError();
  const [data, setData] = useState<PosPanelResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [opciones, setOpciones] = useState<PosTenantOption[]>([]);
  // La contraseña temporal del dueño solo existe en la respuesta del alta: si no se copia
  // ahora, ya no se puede recuperar (hay que reemitirla). Por eso se queda fija en pantalla.
  const [tempPass, setTempPass] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await modulesApi.getPos(tenantId)); }
    catch { setData({ linked: false }); }      // sin POS enlazado no es un error que mostrar
    finally { setLoading(false); }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  // Hasta F4 esta sección tenía sus propios interruptores, que escribían en el POS. Ya no:
  // los módulos de caja viven en la lista de arriba, con todos los demás del negocio. Dos sitios
  // para lo mismo era la contradicción que quedaba de tener el POS aparte.

  // ── Negocio SIN POS: se ofrece activarlo o casarlo con un gemelo que ya exista ──
  const provisionar = async () => {
    setBusyKey('__provision__');
    try {
      const r = await modulesApi.provisionPos(tenantId);
      setTempPass(r.owner_temp_password);
      setConfirming(false);
      await load();
    } catch (e: any) {
      addError(`No se pudo activar el POS: ${e.message}`, undefined, 'posProvision');
      setLinkOpen(true);                       // casi siempre es "ese slug ya existe allá"
      await cargarOpciones();
    } finally { setBusyKey(null); }
  };

  const cargarOpciones = async () => {
    try { const r = await modulesApi.listPosTenants(); setOpciones(r.tenants || []); }
    catch { setOpciones([]); }
  };

  const enlazar = async (posId: number) => {
    setBusyKey('__link__');
    try { await modulesApi.linkPos(tenantId, posId); setLinkOpen(false); await load(); }
    catch (e: any) { addError(`No se pudo enlazar: ${e.message}`, undefined, 'posLink'); }
    finally { setBusyKey(null); }
  };

  if (loading) return null;                    // sin parpadeo: aparece cuando hay algo que decir

  if (!data || !data.linked) {
    if (data && data.available === false) return null;   // puente apagado → ni mencionarlo
    return (
      <div className="bg-surface shadow sm:rounded-lg overflow-hidden">
        <div className="px-4 py-2.5 border-b border-line bg-surface-2 flex items-center gap-2">
          <Store className="h-4 w-4 text-content-3 flex-shrink-0" />
          <h2 className="text-xs font-semibold uppercase tracking-wider text-content-3">Punto de venta</h2>
        </div>
        <div className="px-4 py-3.5 text-sm text-content-2">
          {!confirming && !linkOpen && (
            <div className="flex flex-wrap items-center gap-3">
              <span>Este negocio no usa el POS de Lealify.</span>
              <button onClick={() => setConfirming(true)}
                className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium rounded-md border border-line text-content-2 hover:bg-surface-2">
                Activar POS
              </button>
              <button onClick={() => { setLinkOpen(true); cargarOpciones(); }}
                className="text-xs text-accent hover:underline">
                enlazar con uno existente
              </button>
            </div>
          )}
          {confirming && (
            <div className="space-y-2.5">
              <p>Se dará de alta en el POS con el mismo nombre y su dueño actual, con los módulos básicos encendidos. Se puede suspender o desenlazar después.</p>
              <div className="flex items-center gap-2">
                <button onClick={provisionar} disabled={busyKey === '__provision__'}
                  className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium rounded-md bg-accent text-white disabled:opacity-50">
                  {busyKey === '__provision__' ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : null}
                  Confirmar
                </button>
                <button onClick={() => setConfirming(false)}
                  className="px-2.5 py-1.5 text-xs font-medium rounded-md border border-line text-content-2 hover:bg-surface-2">
                  Cancelar
                </button>
              </div>
            </div>
          )}
          {linkOpen && (
            <div className="space-y-2.5">
              <p className="text-xs text-content-3">Clientes que ya existen en el POS:</p>
              {opciones.length === 0 && <p className="text-xs text-content-3">No hay ninguno disponible.</p>}
              <ul className="divide-y divide-line border border-line rounded-md">
                {opciones.map((o) => (
                  <li key={o.id} className="px-3 py-2 flex items-center justify-between gap-3 text-sm">
                    <span className="min-w-0 truncate">
                      {o.name} <span className="text-content-3 text-xs">· {o.slug}</span>
                      {o.linked_to && <span className="text-content-3 text-xs"> — ya enlazado a «{o.linked_to.name}»</span>}
                    </span>
                    <button onClick={() => enlazar(o.id)} disabled={!!o.linked_to || busyKey === '__link__'}
                      className="px-2.5 py-1 text-xs font-medium rounded-md border border-line text-content-2 hover:bg-surface-2 disabled:opacity-40">
                      Enlazar
                    </button>
                  </li>
                ))}
              </ul>
              <button onClick={() => setLinkOpen(false)} className="text-xs text-content-3 hover:underline">Cancelar</button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface shadow sm:rounded-lg overflow-hidden">
      <div className="px-4 py-2.5 border-b border-line bg-surface-2 flex items-center gap-2">
        <Store className="h-4 w-4 text-content-3 flex-shrink-0" />
        <div className="min-w-0 flex-1">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-content-3">
            Punto de venta
          </h2>
          <p className="text-xs text-content-3 mt-0.5 normal-case tracking-normal font-normal">
            {data.available && data.tenant
              ? `${data.tenant.name} · ${data.tenant.branches} sucursal(es) · ${data.tenant.devices} dispositivo(s) · estado ${data.tenant.status}`
              : 'Servicio del POS'}
          </p>
          {data.health && <PosHealthBadge h={data.health} />}
        </div>
        {/* «Ver como» solo existe del lado de Lealify (crea sesión en lealify-base), así que
            desde aquí lo útil es el acceso directo a su panel del POS. Desde F3 el dueño entra
            ahí con su MISMA contraseña de Lealify. */}
        <a href="https://pos.lealify.com/owner/" target="_blank" rel="noopener noreferrer"
          className="flex-shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-md border border-line text-content-2 hover:bg-surface-2">
          Abrir su POS <ExternalLink className="h-3 w-3" />
        </a>
      </div>

      {tempPass && (
        <div className="px-4 py-3 text-sm bg-green-50 dark:bg-green-500/10 text-green-800 dark:text-green-300 border-b border-green-200 dark:border-green-500/30">
          <p className="font-medium">POS activado.</p>
          <p className="mt-0.5">
            Contraseña temporal del dueño: <code className="px-1.5 py-0.5 rounded bg-surface text-content font-mono">{tempPass}</code>
            {' '}— cópiala ahora, no se puede volver a ver.
          </p>
        </div>
      )}
      {!data.available ? (
        <div className="px-4 py-3 text-sm text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-500/10 flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>
            No se pudo consultar el POS ahora mismo{data.error ? `: ${data.error}` : ''}. La caja sigue
            funcionando con su propia configuración; vuelve a abrir esta página en un momento.
          </span>
        </div>
      ) : (
        <div className="px-4 py-3 text-sm text-content-2 flex items-start gap-2">
          <ShieldCheck className="h-4 w-4 mt-0.5 flex-shrink-0 text-content-3" />
          <span>
            Sus módulos de caja se administran <strong>arriba</strong>, en la misma lista que el resto
            (los que empiezan por <code className="px-1 py-0.5 rounded bg-surface-2 font-mono text-xs">pos_</code>).
            La caja los lee de aquí.
          </span>
        </div>
      )}
    </div>
  );
};

const TenantModulesPage: React.FC = () => {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { addError } = useError();

  const [tenant, setTenant] = useState<TenantInfo | null>(null);
  const [modules, setModules] = useState<ModuleRow[]>([]);
  const [schemaMeta, setSchemaMeta] = useState<Record<string, ModuleManifestMeta>>({});
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [drawerKey, setDrawerKey] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!currentUser || currentUser.role !== 'admin') { setLoading(false); return; }
    setLoading(true);
    try {
      const [data, schema] = await Promise.all([
        modulesApi.getTenantModules(id),
        modulesApi.getSettingsSchema().catch(() => ({} as Record<string, ModuleManifestMeta>)),
      ]);
      setTenant(data.tenant);
      setModules((data.modules || []).slice().sort((a, b) => a.sort - b.sort));
      setSchemaMeta(schema);
    } catch (e: any) {
      addError(`No se pudieron cargar los módulos: ${e.message}`, undefined, 'tenantModules');
    } finally {
      setLoading(false);
    }
  }, [id, currentUser, addError]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (m: ModuleRow) => {
    if (m.is_core === 1) return; // núcleo no se apaga
    const on = !isModuleActive(m);
    setBusyKey(m.module_key);
    // Optimista
    setModules((prev) => prev.map((x) => x.module_key === m.module_key ? { ...x, entitled: on ? 1 : 0, enabled: on ? 1 : 0 } : x));
    try {
      await modulesApi.setModule(id, m.module_key, on);
      setSuccess(`${m.name}: ${on ? 'activado' : 'desactivado'}`);
      setTimeout(() => setSuccess(null), 2500);
    } catch (e: any) {
      // Rollback
      setModules((prev) => prev.map((x) => x.module_key === m.module_key ? m : x));
      addError(`No se pudo cambiar ${m.name}: ${e.message}`, undefined, 'toggleModule');
    } finally {
      setBusyKey(null);
    }
  };

  // "Ver todos": muestra también los módulos que NO aplican a este giro (en gris, para inspección).
  const [showAll, setShowAll] = useState(false);

  // ¿El módulo aplica a este negocio? El backend manda `applies`; si faltara (backend viejo),
  // se calcula con el mismo criterio (fallback retrocompatible).
  const appliesOf = useCallback((m: ModuleRow): boolean => {
    if (typeof m.applies === 'number') return m.applies === 1;
    const v = (tenant?.vertical as string) || 'lealtad';
    if (m.kind === 'vertical') return !!m.vertical && m.vertical === v;
    const meta = schemaMeta[m.module_key];
    if (m.kind === 'cross' && meta) return moduleAppliesToVertical(meta, v);
    return true;
  }, [tenant, schemaMeta]);

  const grouped = useMemo(() => {
    const by: Record<string, ModuleRow[]> = {};
    for (const m of modules) {
      const hasSettings = !!schemaMeta[m.module_key];
      const ok = appliesOf(m);
      // No aplica a esta vertical (p.ej. Odontograma en una cafetería): se OCULTA por defecto,
      // aunque esté activo por datos viejos. Con "Ver todos" cae en "No aplican a este giro".
      if (!ok && !showAll) continue;
      const cat = ok ? moduleCategory(m, hasSettings) : 'no_aplica';
      (by[cat] = by[cat] || []).push(m);
    }
    return CAT_ORDER.filter((k) => by[k]?.length).map((k) => [k, by[k]] as const);
  }, [modules, schemaMeta, showAll, appliesOf]);

  // Colapso por categoría (persistente en la sesión de la página).
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(CAT_COLLAPSED_DEFAULT);
  const toggleCat = (cat: string) => setCollapsed((c) => ({ ...c, [cat]: !c[cat] }));

  if (!currentUser || currentUser.role !== 'admin') {
    return <Layout><div className="p-4 text-sm text-content-3">Solo para administradores de plataforma.</div></Layout>;
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center gap-3 animate-fade-in-up">
          <button onClick={() => navigate(`/dashboard/admin/negocios/${id}`)} title="Volver a la ficha del negocio" className="p-2 rounded-md hover:bg-surface-2 text-content-2"><ArrowLeft className="h-5 w-5" /></button>
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-content flex items-center gap-2 truncate">
              Módulos del negocio
            </h1>
            {tenant && (
              <div className="flex items-center gap-2 mt-1">
                <span className="text-sm text-content-2 truncate">{tenant.name}</span>
                <VerticalBadge vertical={(tenant.vertical as string) || 'lealtad'} />
              </div>
            )}
          </div>
          <button
            onClick={() => setShowAll((s) => !s)}
            className={`ml-auto flex-shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md border transition-colors ${
              showAll ? 'bg-accent/10 border-accent/40 text-accent' : 'border-line text-content-2 hover:bg-surface-2'
            }`}
            title="Mostrar también los módulos que no aplican a este giro"
          >
            {showAll ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
            {showAll ? 'Viendo todos' : 'Ver todos'}
          </button>
        </div>

        {success && (
          <div className="bg-green-50 dark:bg-green-500/10 border border-green-200 dark:border-green-500/30 text-green-800 dark:text-green-300 rounded-md p-2.5 text-sm flex items-center animate-fade-in">
            <Check className="h-4 w-4 mr-1.5" />{success}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-16 text-content-3"><Loader2 className="h-6 w-6 animate-spin mr-2" />Cargando módulos…</div>
        ) : (
          <div className="space-y-6">
            {grouped.map(([cat, mods]) => {
              const info = CAT_LABEL[cat] || { title: cat, desc: '' };
              // Un negocio con caja enlazada tiene dos verticales, y sus módulos caen todos en
              // este grupo: el rótulo las nombra a las dos para que no parezca que «Venta
              // (terminal)» es una función de lealtad.
              const verticales = [VERTICAL_LABEL[(tenant?.vertical as string) || ''] || (tenant?.vertical as string) || '']
                .concat(mods.some((m) => m.module_key.startsWith('pos_')) ? ['POS'] : [])
                .filter(Boolean);
              const title = cat === 'vertical'
                ? `De este negocio (${verticales.join(' + ')})`
                : info.title;
              const isOpen = !collapsed[cat];
              return (
              <div key={cat} className="bg-surface shadow sm:rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleCat(cat)}
                  className="w-full px-4 py-2.5 border-b border-line bg-surface-2 hover:bg-surface-2/70 text-left flex items-center gap-2"
                  aria-expanded={isOpen}
                >
                  {isOpen ? <ChevronDown className="h-4 w-4 text-content-3 flex-shrink-0" /> : <ChevronRight className="h-4 w-4 text-content-3 flex-shrink-0" />}
                  <div className="min-w-0 flex-1">
                    <h2 className="text-xs font-semibold uppercase tracking-wider text-content-3">
                      {title} <span className="text-content-3/70">· {mods.length}</span>
                    </h2>
                    {info.desc && <p className="text-xs text-content-3 mt-0.5 normal-case tracking-normal font-normal">{info.desc}</p>}
                  </div>
                </button>
                {isOpen && (
                <ul className="divide-y divide-line">
                  {mods.map((m) => {
                    const active = isModuleActive(m);
                    const meta = schemaMeta[m.module_key];
                    const ready = cat === 'listos';
                    const prep = cat === 'preparacion';
                    const applies = appliesOf(m);
                    const canConfig = applies && !!meta && active && moduleAppliesToVertical(meta, (tenant?.vertical as string) || 'lealtad');
                    return (
                      <li key={m.module_key} className={`px-4 py-3 flex items-center justify-between gap-3 ${!applies ? 'opacity-60' : ''}`}>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-medium text-content truncate">{m.name}</span>
                            {!applies && (
                              <span className="inline-flex items-center text-[11px] font-medium px-1.5 py-0.5 rounded bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-500/30">
                                no aplica a este giro
                              </span>
                            )}
                            {ready && applies && (
                              <span className="inline-flex items-center text-[11px] font-medium px-1.5 py-0.5 rounded bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-300">
                                <Check className="h-3 w-3 mr-0.5" />Listo
                              </span>
                            )}
                            {prep && (
                              <span className="inline-flex items-center text-[11px] font-medium px-1.5 py-0.5 rounded bg-surface-2 text-content-3 border border-line">
                                próximamente
                              </span>
                            )}
                            {m.is_core === 1 && (
                              <span className="inline-flex items-center text-xs text-content-3" title="Módulo núcleo: siempre activo">
                                <ShieldCheck className="h-3.5 w-3.5 mr-0.5" />núcleo
                              </span>
                            )}
                          </div>
                          {MODULE_DESC[m.module_key] && (
                            <p className="text-xs text-content-3 mt-0.5">{MODULE_DESC[m.module_key]}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          {canConfig && (
                            <button onClick={() => setDrawerKey(m.module_key)}
                              className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium rounded-md border border-line text-content-2 hover:bg-surface-2">
                              <Settings className="h-3.5 w-3.5 mr-1" />Configurar
                            </button>
                          )}
                          {busyKey === m.module_key
                            ? <Loader2 className="h-5 w-5 animate-spin text-content-3" />
                            : <Toggle checked={active} disabled={m.is_core === 1 || prep || (!applies && !active)} onChange={() => toggle(m)} label={`Activar ${m.name}`} />}
                        </div>
                      </li>
                    );
                  })}
                </ul>
                )}
              </div>
              );
            })}
            {/* El POS del negocio, si lo tiene enlazado (servicio aparte, ver PosSection). */}
            <PosSection tenantId={id} />
          </div>
        )}
      </div>

      {drawerKey && (
        <SettingsDrawer tenantId={id} moduleKey={drawerKey} onClose={() => setDrawerKey(null)}
          onSaved={() => { setSuccess('Configuración guardada'); setTimeout(() => setSuccess(null), 2500); }} />
      )}
    </Layout>
  );
};

export default TenantModulesPage;
